import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Heart, Star, Target, Briefcase, Clock, Users, ArrowRight, Sparkles, Compass } from 'lucide-react';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function Home() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const categories = [
    { key: 'passion', icon: Heart, color: 'bg-[#FB6823]' },
    { key: 'talent', icon: Star, color: 'bg-[#A4187A]' },
    { key: 'mission', icon: Target, color: 'bg-[#00C853]' },
    { key: 'profession', icon: Briefcase, color: 'bg-[#2196F3]' }
  ];

  return (
    <div className="min-h-screen bg-[#EBEBEB] flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="px-6 md:px-12 py-12 md:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F3E5F5] rounded-full border-2 border-[#1D1D1D] mb-6">
                  <Sparkles className="w-4 h-4 text-[#A4187A]" />
                  <span className="text-sm font-semibold text-[#A4187A]">{t('targetAge')}</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#1D1D1D] mb-6 leading-tight">
                  {t('heroTitle')}
                </h1>
                
                <p className="text-lg md:text-xl text-[#1D1D1D]/70 mb-8 leading-relaxed">
                  {t('heroSubtitle')}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => navigate('/questionnaire')}
                    className="neo-button-primary flex items-center justify-center gap-2"
                    data-testid="start-test-btn"
                  >
                    {t('startButton')}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  
                  <button
                    onClick={() => navigate('/explore')}
                    className="neo-button-outline flex items-center justify-center gap-2"
                    data-testid="explore-careers-btn"
                  >
                    <Compass className="w-5 h-5" />
                    {language === 'fr' ? 'Explorer les métiers' : 'Ontdek beroepen'}
                  </button>
                </div>
                
                <div className="flex items-center gap-6 mt-8">
                  <div className="flex items-center gap-2 text-[#1D1D1D]/60">
                    <Clock className="w-5 h-5" />
                    <span className="text-sm font-medium">{t('duration')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#1D1D1D]/60">
                    <Users className="w-5 h-5" />
                    <span className="text-sm font-medium">{t('targetAge')}</span>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative hidden md:block"
              >
                <div className="relative w-full aspect-square max-w-lg mx-auto">
                  {/* Ikigai Venn Diagram */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative w-80 h-80">
                      {/* Circles */}
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full bg-[#FB6823]/20 border-4 border-[#FB6823]" />
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-40 h-40 rounded-full bg-[#A4187A]/20 border-4 border-[#A4187A]" />
                      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-40 h-40 rounded-full bg-[#00C853]/20 border-4 border-[#00C853]" />
                      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-40 h-40 rounded-full bg-[#2196F3]/20 border-4 border-[#2196F3]" />
                      
                      {/* Center */}
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-white border-4 border-[#1D1D1D] flex items-center justify-center shadow-lg z-10">
                        <span className="font-black text-[#A4187A] text-lg">Ikigai</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* What is Ikigai Section */}
        <section id="learn-more" className="px-6 md:px-12 py-16 bg-[#F3E5F5]">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-black text-[#1D1D1D] mb-4">
                {t('whatIsIkigai')}
              </h2>
              <p className="text-lg text-[#1D1D1D]/70 max-w-3xl mx-auto">
                {t('ikigaiDescription')}
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {categories.map(({ key, icon: Icon, color }) => (
                <motion.div
                  key={key}
                  variants={staggerItem}
                  className="neo-card-interactive"
                  data-testid={`category-${key}`}
                >
                  <div className={`category-icon ${key} mb-4`}>
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1D1D1D] mb-2">
                    {t(key)}
                  </h3>
                  <p className="text-[#1D1D1D]/60">
                    {t(`${key}Desc`)}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* How it works Section */}
        <section className="px-6 md:px-12 py-16">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-black text-[#1D1D1D] mb-4">
                {t('howItWorks')}
              </h2>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {[1, 2, 3].map((step) => (
                <motion.div
                  key={step}
                  variants={staggerItem}
                  className="neo-card text-center"
                  data-testid={`step-${step}`}
                >
                  <div className="w-16 h-16 rounded-full bg-[#A4187A] text-white flex items-center justify-center text-2xl font-black mx-auto mb-6 border-2 border-[#1D1D1D]"
                    style={{ boxShadow: '3px 3px 0px 0px #1D1D1D' }}
                  >
                    {step}
                  </div>
                  <h3 className="text-xl font-bold text-[#1D1D1D] mb-2">
                    {t(`step${step}`)}
                  </h3>
                  <p className="text-[#1D1D1D]/60">
                    {t(`step${step}Desc`)}
                  </p>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-center mt-12"
            >
              <button
                onClick={() => navigate('/questionnaire')}
                className="neo-button-secondary flex items-center justify-center gap-2 mx-auto"
                data-testid="start-test-btn-2"
              >
                {t('startButton')}
                <ArrowRight className="w-5 h-5" />
              </button>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
